import React, { useState } from 'react';
import '../styles/Login.css';

const Login = ({ onLogin }) => {
  const [step, setStep] = useState('login'); // 'login' or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: ''
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.email || !formData.password) {
      showMessage('Please fill in all fields', 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.requiresOTP) {
          setStep('otp');
          showMessage('Verification code sent to your email', 'success');
        } else {
          // Direct login success (shouldn't happen with OTP flow)
          localStorage.setItem('authToken', result.token);
          onLogin(result.user);
        }
      } else {
        showMessage(result.message || 'Login failed', 'error');
      }
    } catch (error) {
      console.error('Login error:', error);
      showMessage('Network error. Please try again.', 'error');
    }

    setLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.otp || formData.otp.length !== 6) {
      showMessage('Please enter a valid 6-digit code', 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          otp: formData.otp
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store token in sessionStorage for 30-minute session that expires when browser closes
        sessionStorage.setItem('authToken', result.token);
        showMessage('Login successful!', 'success');
        setTimeout(() => {
          onLogin(result.user);
        }, 1000);
      } else {
        showMessage(result.message || 'Invalid verification code', 'error');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      showMessage('Network error. Please try again.', 'error');
    }

    setLoading(false);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        showMessage('New verification code sent to your email', 'success');
      } else {
        showMessage(result.message || 'Failed to resend code', 'error');
      }
    } catch (error) {
      showMessage('Network error. Please try again.', 'error');
    }

    setLoading(false);
  };

  const handleBackToLogin = () => {
    setStep('login');
    setFormData({ ...formData, otp: '' });
    setMessage({ text: '', type: '' });
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-header">
          <div className="logo-section">
            <img 
              src="/assets/gls-logo-white.svg" 
              alt="GLS Logo" 
              className="gls-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h1>Hub Monitor</h1>
          <p>Parcel Logistics Search System</p>
        </div>

        <div className="login-card">
          {step === 'login' ? (
            <>
              <h2>Sign In</h2>
              <p className="login-subtitle">Enter your credentials to access the system</p>
              
              <form onSubmit={handleLoginSubmit} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary login-btn"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2>Email Verification</h2>
              <p className="login-subtitle">
                We've sent a 6-digit verification code to<br />
                <strong>{formData.email}</strong>
              </p>
              
              <form onSubmit={handleOTPSubmit} className="login-form">
                <div className="form-group">
                  <label htmlFor="otp">Verification Code</label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    required
                    disabled={loading}
                    className="otp-input"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary login-btn"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>

              <div className="otp-actions">
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={handleResendOTP}
                  disabled={loading}
                >
                  Resend Code
                </button>
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={handleBackToLogin}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </>
          )}

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </div>

        <div className="login-footer">
          <p>&copy; 2025 GLS Hub Monitor. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
